"use client"
import { useNumberFormatter } from "@opensea/i18n"
import {
  Media,
  Separator,
  SideSheetBody,
  SideSheetHeader,
  SideSheetHeaderTitle,
  UnstyledButton,
  useToasts,
} from "@opensea/ui-kit"
import { Close, ExpandMore } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../components/Button"
import { useAddress } from "../../../polychain/hooks"
import { FlowCallbackActions } from "../../../stores/flowCallbackStore"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { SwapFlowInitialCurrency, SwapFlowState } from "../../flow-state"
import { SwapConfiguration } from "./components/SwapConfiguration"
import { SwapModule } from "./components/SwapModule"
import type { SwapReceipt } from "./hooks/useSwapReceipt"
import { useSwapContext } from "./provider/SwapContext"
import { SwapProvider } from "./provider/SwapProvider"
import { getSwapReceiptFailReasonMessage } from "./utils"

type SwapFlowProps = {
  onBack: (() => void) | undefined
  onClose: () => void
  initialFromCurrency: SwapFlowInitialCurrency | undefined
  initialToCurrency: SwapFlowInitialCurrency | undefined
}

export const SwapFlow = ({
  onBack,
  onClose,
  initialFromCurrency,
  initialToCurrency,
}: SwapFlowProps) => {
  const t = useTranslations("wallet.SwapFlow")
  return (
    <SwapProvider
      initialFromCurrency={initialFromCurrency}
      initialToCurrency={initialToCurrency}
      source="wallet-sidesheet"
    >
      <SideSheetHeader className="gap-2">
        {onBack && (
          <UnstyledButton onClick={onBack}>
            <ExpandMore className="rotate-90" size={24} title={t("back")} />
          </UnstyledButton>
        )}
        <SideSheetHeaderTitle>{t("swap")}</SideSheetHeaderTitle>
        <SwapConfiguration className="ml-auto" />
        <Media lessThan="md">
          <Button
            className="h-8 p-1.5"
            onClick={() => {
              onClose()
            }}
            variant="ghost"
          >
            <Close size={20} title={t("close")} />
          </Button>
        </Media>
      </SideSheetHeader>
      <Separator />
      <SwapFlowBase />
    </SwapProvider>
  )
}

const SwapFlowBase = () => {
  const t = useTranslations("wallet.SwapFlow")
  const { setContainer } = useSwapContext()

  const address = useAddress()
  const { flow } = useSnapshot(FlowState)
  const buttonColor = (flow as SwapFlowState)?.buttonColor
  const { showSuccessMessage, showErrorMessage } = useToasts()

  const numberFormatter = useNumberFormatter()
  const onCompleted = (receipt: SwapReceipt) => {
    if (receipt.status === "SUCCESS") {
      showSuccessMessage(
        t("successMessage", {
          quantity: numberFormatter(receipt.toQuantity),
          symbol: receipt.toAsset.symbol,
        }),
        {
          duration: 3000,
        },
      )
      FlowActions.startFlow({
        type: "view-wallet",
        address: address ?? "",
        tab: "crypto",
      })
    } else if (receipt.status === "FAILED") {
      FlowCallbackActions.failAll(flow as SwapFlowState, receipt)
      showErrorMessage(
        t("failureMessage", {
          status: receipt.crossChainRefunded ? t("refunded") : t("failed"),
          reason: getSwapReceiptFailReasonMessage(
            receipt.failReason ?? "UNKNOWN",
          ),
        }),
        {
          duration: 3000,
        },
      )
    }
  }

  return (
    <SideSheetBody
      className="flex grow flex-col p-4 lg:px-4"
      ref={setContainer}
    >
      <SwapModule
        buttonColor={buttonColor}
        onCompleted={onCompleted}
        surface="sheet"
      />
    </SideSheetBody>
  )
}
